import React, { useRef, useEffect, useState } from 'react';
import { Tree, TreeNode } from 'react-organizational-chart';
import { Row, Col } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import '../../../../css/DppWidget.css';
import getLocalizedText from '../../../Utils';

const Node = ({ label, assetIds = [], compact = false }) => (
  <div
    style={{
      padding: compact ? '12px 14px' : '21px 20px',
      background: '#003E81',
      color: 'white',
      borderRadius: compact ? '14px' : '20px',
      display: 'inline-block',
      fontWeight: '600',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      minWidth: compact ? '140px' : '210px',
      maxWidth: compact ? '190px' : 'none',
      textAlign: 'center',
    }}
  >
    <div style={{ fontSize: compact ? '13px' : '1.1em', marginBottom: assetIds.length ? (compact ? '4px' : '6px') : '0', lineHeight: '1.3' }}>
      {label}
    </div>

    {assetIds.length > 0 && (
      <div style={{ fontSize: compact ? '11px' : '0.75em', textAlign: 'center', width: '100%' }}>
        {assetIds.map((asset, idx) => (
          <div key={idx} style={{ marginBottom: compact ? '2px' : '4px', color: '#e2e8f0' }}>
            <span style={{ opacity: 0.8, fontSize: compact ? '10px' : '11px' }}>
              <FormattedMessage
                id={`billOfMaterials.${asset.name}`}
                defaultMessage="{name}"
                values={{ name: asset.name }}
              />
            </span>{' '}
            <span style={{ fontFamily: 'monospace', fontSize: compact ? '11px' : '12px' }}>
              {asset.value || '-'}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const BillOfMaterialsWidget = ({ submodel }) => {
  const intl = useIntl();
  const currentLocale = intl.locale;
  const scrollRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [fades, setFades] = useState({ left: false, right: false });

  const { rootNode, archeType } = React.useMemo(() => {
    const archeTypeEl = submodel.submodelElements.find(el => el.idShort === 'ArcheType');

    const entry = submodel.submodelElements.find(
      el => el.modelType === 'Entity' && el.semanticId?.keys?.[0]?.value.includes('EntryNode')
    );

    if (!entry) {
      return { rootNode: null, archeType: 'Unknown' };
    }

    const buildNode = (entity) => {
      const displayName = entity.displayName
        ? getLocalizedText(entity.displayName, entity.idShort || 'Unnamed', currentLocale)
        : entity.idShort || 'Unnamed';

      const assetIds = (entity.specificAssetIds || []).map(asset => ({
        name: asset.name || 'UnknownKey',
        value: asset.value || '',
      }));

      return { label: displayName, assetIds, children: [] };
    };

    const root = buildNode(entry);

    const populateChildren = (node, aasEntity) => {
      const statements = aasEntity.statements || [];
      node.children = statements
        .filter(s => s.modelType === 'Entity')
        .map(childEntity => {
          const childNode = buildNode(childEntity);
          populateChildren(childNode, childEntity);
          return childNode;
        });
    };

    populateChildren(root, entry);

    return {
      archeType: archeTypeEl?.value || 'Unknown',
      rootNode: root,
    };
  }, [submodel, currentLocale]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const updateFades = () => {
      const overflows = el.scrollWidth > el.clientWidth + 4;
      setFades({
        left: overflows && el.scrollLeft > 2,
        right: overflows && el.scrollLeft < el.scrollWidth - el.clientWidth - 2,
      });
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setShowScrollHint(el.scrollWidth > el.clientWidth + 4);
      el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
      updateFades();
    };

    el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
    handleResize();

    el.addEventListener('scroll', updateFades);
    window.addEventListener('resize', handleResize);
    return () => {
      el.removeEventListener('scroll', updateFades);
      window.removeEventListener('resize', handleResize);
    };
  }, [rootNode]);

  if (!rootNode) {
    return (
      <div className="dpp-widget p-4 text-muted">
        No hierarchical structure found (missing EntryNode)
      </div>
    );
  }

  const renderTree = (node) => (
    <TreeNode
      key={node.label + '_' + node.assetIds.map(a => a.value).join('_')}
      label={<Node label={node.label} assetIds={node.assetIds} compact={isMobile} />}
    >
      {node.children?.map(child => renderTree(child))}
    </TreeNode>
  );

  return (
    <div className="dpp-widget">
      <Row className="g-0">
        <Col xs={12} className="p-4 p-xl-5">
          <h2 className="dpp-widget-title mb-4">
            <FormattedMessage id="billOfMaterials.title" defaultMessage="Bill of Materials" />
          </h2>

          {showScrollHint && (
            <div style={{ fontSize: '12px', color: '#aaa', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>←</span>
              <span><FormattedMessage id="billOfMaterials.scrollHint" defaultMessage="Scroll to explore" /></span>
              <span>→</span>
            </div>
          )}

          <div style={{ position: 'relative' }}>
            {/* Left fade */}
            <div style={{
              position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px',
              background: 'linear-gradient(to right, white, transparent)',
              pointerEvents: 'none', zIndex: 1,
              opacity: fades.left ? 1: 0,
              transition: 'opacity 0.2s',
            }} />
            {/* Right fade */}
            <div style={{
              position: 'absolute', right: 0, top: 0, bottom: 0, width: '4px',
              background: 'linear-gradient(to left, white, transparent)',
              pointerEvents: 'none', zIndex: 1,
              opacity: fades.right ? 1 : 0,
              transition: 'opacity 0.2s',
            }} />

            <div
              ref={scrollRef}
              style={{
                overflowX: 'auto',
                overflowY: 'visible',
                WebkitOverflowScrolling: 'touch',
                padding: '25px 0',
              }}
            >
              <div style={{ width: 'max-content', minWidth: '100%', paddingBottom: '8px' }}>
                <Tree
                  lineWidth="2px"
                  lineColor="#94a3b8"
                  lineBorderRadius="20px"
                  label={<Node label={rootNode.label} assetIds={rootNode.assetIds} compact={isMobile} />}
                >
                  {rootNode.children?.map(child => renderTree(child))}
                </Tree>
              </div>
            </div>
          </div>

          <div className="mt-4 text-muted small">
            <strong>ArcheType:</strong> {archeType}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default BillOfMaterialsWidget;
